import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Modal, InputGroup, Alert } from 'react-bootstrap';
import { pixelService, domainService } from '../../services/api';
import './Pixels.css';

const PixelList = () => {
  const [pixels, setPixels] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPixel, setEditingPixel] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    pixelId: '',
    token: '',
    domainId: '',
    viewPage: 'ViewPage',
    interactedBot: 'InteractedBot',
    enteredChannel: 'EnteredChannel'
  });
  const [error, setError] = useState('');
  const [testingPixel, setTestingPixel] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Log de montagem do componente
  useEffect(() => {
    console.log('[PixelList] Componente montado');
    return () => {
      console.log('[PixelList] Componente desmontado');
    };
  }, []);

  // Carregar pixels e domínios
  useEffect(() => {
    fetchPixels();
    fetchDomains();
  }, []);

  const fetchPixels = async () => {
    console.log('[PixelList] Iniciando busca de pixels');
    setLoading(true);
    setError('');
    
    try {
      const response = await pixelService.getAll();
      console.log('[PixelList] Resposta pixels recebida:', response);
      
      let pixelsArray = [];
      if (Array.isArray(response.data)) {
        pixelsArray = response.data;
      } else if (response.data.pixels && Array.isArray(response.data.pixels)) {
        pixelsArray = response.data.pixels;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        pixelsArray = response.data.data;
      }

      // Processar os pixels
      const processedPixels = processPixels(pixelsArray);
      console.log('[PixelList] Pixels processados:', processedPixels);
      setPixels(processedPixels);
    } catch (error) {
      console.error('[PixelList] Erro ao carregar pixels:', error);
      setError('Falha ao carregar pixels. Por favor, tente novamente.');
      setPixels([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDomains = async () => {
    console.log('[PixelList] Iniciando busca de domínios');
    try {
      const response = await domainService.getAll();
      console.log('[PixelList] Resposta domínios recebida:', response);
      
      let domainsArray = [];
      if (Array.isArray(response.data)) {
        domainsArray = response.data;
      } else if (response.data.domains && Array.isArray(response.data.domains)) {
        domainsArray = response.data.domains;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        domainsArray = response.data.data;
      }
      
      console.log('[PixelList] Domínios processados:', domainsArray);
      
      setDomains(domainsArray);
    } catch (error) {
      console.error('[PixelList] Erro ao carregar domínios:', error);
      setDomains([]);
    }
  };

  // Filtrar pixels com base na pesquisa
  const filteredPixels = Array.isArray(pixels)
    ? pixels.filter(pixel => {
        const searchTermLower = searchTerm.toLowerCase();
        return (
          pixel?.pixelId?.toLowerCase().includes(searchTermLower) ||
          pixel?.nome?.toLowerCase().includes(searchTermLower) ||
          pixel?.domain?.url?.toLowerCase().includes(searchTermLower)
        );
      })
    : [];

  // Formatar ID do Pixel para exibição
  const formatPixelId = (pixelId) => {
    if (!pixelId) return '';
    if (pixelId.length <= 8) return pixelId;
    return `${pixelId.substring(0, 4)}...${pixelId.substring(pixelId.length - 4)}`;
  };

  // Obter URL do domínio pelo ID
  const getDomainUrl = (domainId) => {
    try {
      // Se o domainId é um objeto completo
      if (typeof domainId === 'object' && domainId.url) {
        return domainId.url;
      }

      // Se não, procurar o pixel e seu domínio
      const pixel = pixels.find(p => p._id === domainId);
      if (pixel?.domain?.url) {
        return pixel.domain.url;
      }

      // Por último, tentar encontrar nos domínios
      const domain = domains.find(d => d._id === domainId);
      return domain?.url || 'Domínio não encontrado';
    } catch (error) {
      console.error('Erro ao buscar domínio:', error);
      return 'Domínio não encontrado';
    }
  };

  // Abrir modal para adicionar novo pixel
  const handleAddPixel = () => {
    setEditingPixel(null);
    setFormData({
      nome: '',
      pixelId: '',
      token: '',
      domainId: domains.length > 0 ? domains[0]._id : '',
      viewPage: 'ViewPage',
      interactedBot: 'InteractedBot',
      enteredChannel: 'EnteredChannel'
    });
    setShowModal(true);
    setTestResult(null);
  };

  // Abrir modal para editar pixel existente
  const handleEditPixel = (pixel) => {
    console.log('Editando pixel:', pixel);
    setEditingPixel(pixel);
    setFormData({
      nome: pixel.nome || `Pixel ${formatPixelId(pixel.pixelId)}`,
      pixelId: pixel.pixelId,
      token: pixel.token,
      domainId: pixel.domainId,
      viewPage: pixel.viewPage || pixel.ViewPage || 'ViewPage',
      interactedBot: pixel.InteractedBot || pixel.interactedBot || 'InteractedBot',
      enteredChannel: pixel.EnteredChannel || pixel.enteredChannel || 'EnteredChannel'
    });
    setShowModal(true);
    setTestResult(null);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setError('');
    setTestResult(null);
  };

  // Atualizar dados do formulário
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Validar e formatar dados do pixel
  const validateAndFormatPixelData = (data) => {
    // Remover espaços em branco
    const pixelId = data.pixelId.trim();
    const token = data.token.trim();

    // Validar Pixel ID (deve ser numérico)
    if (!/^\d+$/.test(pixelId)) {
      throw new Error('O ID do Pixel deve conter apenas números');
    }

    // Validar Token (não pode estar vazio)
    if (!token) {
      throw new Error('O Token do Pixel é obrigatório');
    }

    // Validar Domínio
    if (!data.domainId) {
      throw new Error('O Domínio é obrigatório');
    }

    // Garantir que os eventos tenham valores padrão se estiverem vazios
    const formattedData = {
      nome: data.nome || `Pixel ${pixelId}`,
      pixelId,
      token,
      domainId: data.domainId,
      viewPage: data.viewPage.trim() || 'ViewPage',
      InteractedBot: data.interactedBot.trim() || 'InteractedBot',
      EnteredChannel: data.enteredChannel.trim() || 'EnteredChannel'
    };

    console.log('Dados formatados do pixel:', formattedData);
    return formattedData;
  };

  // Processar os pixels para garantir que o domínio esteja correto
  const processPixels = (pixelsArray) => {
    return pixelsArray.map(pixel => {
      // Se domainId já é um objeto completo do domínio
      if (pixel.domainId && typeof pixel.domainId === 'object') {
        return {
          ...pixel,
          domain: pixel.domainId,
          domainId: pixel.domainId._id, // Manter o ID separado para referência
          // Garantir que os eventos estejam disponíveis tanto no objeto events quanto como campos individuais
          viewPage: pixel.viewPage || pixel.ViewPage || 'ViewPage',
          interactedBot: pixel.InteractedBot || pixel.interactedBot || 'InteractedBot',
          enteredChannel: pixel.EnteredChannel || pixel.enteredChannel || 'EnteredChannel'
        };
      }
      
      // Se não, procurar o domínio na lista de domínios
      const domain = domains.find(d => d._id === pixel.domainId);
      return {
        ...pixel,
        domain: domain || null,
        // Garantir que os eventos estejam disponíveis tanto no objeto events quanto como campos individuais
        viewPage: pixel.viewPage || pixel.ViewPage || 'ViewPage',
        interactedBot: pixel.InteractedBot || pixel.interactedBot || 'InteractedBot',
        enteredChannel: pixel.EnteredChannel || pixel.enteredChannel || 'EnteredChannel'
      };
    });
  };

  // Testar pixel
  const handleTestPixel = async () => {
    try {
      const validatedData = validateAndFormatPixelData(formData);
      setTestingPixel(true);
      setTestResult(null);
      setError('');
      
      console.log('Testando pixel com dados:', validatedData);
      
      // Buscar o domínio selecionado
      const selectedDomain = domains.find(d => d._id === formData.domainId);
      if (!selectedDomain) {
        throw new Error('Domínio não encontrado');
      }

      const testData = {
        ...validatedData,
        domainId: selectedDomain
      };

      const response = await pixelService.testPixel(testData);
      console.log('Resposta do teste:', response);
      
      if (response.data.results) {
        const successfulTests = response.data.results.filter(r => r.success).length;
        const totalTests = response.data.results.length;
        
        setTestResult({
          success: successfulTests > 0,
          message: `${successfulTests} de ${totalTests} eventos enviados com sucesso!`,
          details: response.data.results.map(r => ({
            type: r.type,
            success: r.success,
            message: r.error || 'Evento enviado com sucesso'
          }))
        });
      } else {
        setTestResult({
          success: true,
          message: 'Pixel testado com sucesso!'
        });
      }
    } catch (error) {
      console.error('Erro ao testar pixel:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.message || error.message || 'Falha ao testar pixel. Verifique os dados e tente novamente.'
      });
    } finally {
      setTestingPixel(false);
    }
  };

  // Salvar pixel (criar ou atualizar)
  const handleSavePixel = async () => {
    try {
      const validatedData = validateAndFormatPixelData(formData);
      console.log('Salvando pixel com dados:', validatedData);
      
      let response;
      if (editingPixel) {
        // Atualizar pixel existente
        console.log('Atualizando pixel existente:', editingPixel._id);
        response = await pixelService.update(editingPixel._id, validatedData);
      } else {
        // Criar novo pixel
        console.log('Criando novo pixel');
        response = await pixelService.create(validatedData);
      }
      
      console.log('Resposta da API:', response);
      
      // Recarregar lista de pixels
      fetchPixels();
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar pixel:', error);
      console.error('Detalhes do erro:', error.response?.data);
      setError(error.response?.data?.message || error.message || 'Falha ao salvar pixel. Por favor, tente novamente.');
    }
  };

  // Excluir pixel
  const handleDeletePixel = async (pixelId) => {
    if (window.confirm('Tem certeza que deseja excluir este pixel?')) {
      try {
        await pixelService.delete(pixelId);
        fetchPixels();
      } catch (error) {
        console.error('Erro ao excluir pixel:', error);
        alert('Falha ao excluir pixel. Por favor, tente novamente.');
      }
    }
  };

  return (
    <Container fluid className="pixels-container">
      <h1 className="mb-4">Pixels</h1>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      
      <Row className="mb-4">
        <Col md={6}>
          <InputGroup>
            <Form.Control
              placeholder="Pesquisar pixels..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={6} className="text-end">
          <Button variant="primary" onClick={handleAddPixel}>
            Adicionar Pixel
          </Button>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : filteredPixels.length === 0 ? (
            <div className="text-center p-5">
              {searchTerm ? 'Nenhum pixel encontrado para a pesquisa' : 'Nenhum pixel cadastrado'}
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Pixel ID</th>
                  <th>Domínio</th>
                  <th>Data de Criação</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredPixels.map(pixel => (
                  <tr key={pixel._id}>
                    <td>{pixel.nome || `Pixel ${formatPixelId(pixel.pixelId)}`}</td>
                    <td>
                      <span title={pixel.pixelId}>{formatPixelId(pixel.pixelId)}</span>
                    </td>
                    <td>{(pixel.domain?.url || pixel.domainId?.url) || 'Domínio não encontrado'}</td>
                    <td>{pixel.createdAt ? new Date(pixel.createdAt).toLocaleDateString() : 'N/A'}</td>
                    <td>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleEditPixel(pixel)}
                      >
                        Editar
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDeletePixel(pixel._id)}
                      >
                        Excluir
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal para adicionar/editar pixel */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingPixel ? 'Editar Pixel' : 'Adicionar Pixel'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className="alert alert-danger">{error}</div>}
          {testResult && (
            <div className={`alert ${testResult.success ? 'alert-success' : 'alert-danger'}`}>
              <p className="mb-2">{testResult.message}</p>
              {testResult.details && (
                <ul className="mb-0">
                  {testResult.details.map((detail, index) => (
                    <li key={index} className={detail.success ? 'text-success' : 'text-danger'}>
                      {detail.type}: {detail.success ? '✓' : '✗'} {!detail.success && `(${detail.message})`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome do Pixel</Form.Label>
              <Form.Control
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                placeholder="Nome para identificação do pixel"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Domínio</Form.Label>
              <Form.Select
                name="domainId"
                value={formData.domainId}
                onChange={handleInputChange}
              >
                <option value="">Selecione um domínio</option>
                {domains.map(domain => (
                  <option key={domain._id} value={domain._id}>
                    {domain.url}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Pixel ID</Form.Label>
              <Form.Control
                type="text"
                name="pixelId"
                value={formData.pixelId}
                onChange={handleInputChange}
                placeholder="ID do Pixel do Facebook"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Token do Pixel</Form.Label>
              <Form.Control
                type="text"
                name="token"
                value={formData.token}
                onChange={handleInputChange}
                placeholder="Token de acesso do Pixel"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Evento de Visualização de Página</Form.Label>
              <Form.Control
                type="text"
                name="viewPage"
                value={formData.viewPage}
                onChange={handleInputChange}
                placeholder="Nome do evento para visualização de página"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Evento de Interação com Bot</Form.Label>
              <Form.Control
                type="text"
                name="interactedBot"
                value={formData.interactedBot}
                onChange={handleInputChange}
                placeholder="Nome do evento para interação com bot"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Evento de Entrada no Canal</Form.Label>
              <Form.Control
                type="text"
                name="enteredChannel"
                value={formData.enteredChannel}
                onChange={handleInputChange}
                placeholder="Nome do evento para entrada no canal"
              />
            </Form.Group>

            <div className="mt-3 text-center">
              <Button 
                variant="info" 
                onClick={handleTestPixel}
                disabled={testingPixel}
              >
                {testingPixel ? 'Testando...' : 'Testar Pixel'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSavePixel}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PixelList; 